import { useState } from 'react';

import { useIsMutating, useQuery } from '@tanstack/react-query';
import { ChevronRightIcon } from 'lucide-react';
import { match } from 'ts-pattern';

import { Button } from '@nesso/ui/primitives/button';

import {
    clientByIdOptions,
    clientContactsOptions,
    clientProfileOptions,
    clientRelationshipsOptions,
    mutationKeys,
    type SelectedClient,
} from '@/entities/client';
import { useHasAccessSources } from '@/entities/practice-member';
import { clientActivityOptions } from '@/entities/session';
import { clientBalanceOptions } from '@/entities/session-charge';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { CenteredSpinner } from '@/shared/ui/indicators/centered-spinner';
import { SheetDescription, SheetTitle } from '@/shared/ui/panels/sheet-panel';

import { ClientDetailsActionBar } from './client-details-chart/client-details-action-bar';
import { ClientDetailsBilling } from './client-details-chart/client-details-billing';
import { ClientDetailsChartActivity } from './client-details-chart/client-details-chart-activity';
import { ClientDetailsContacts } from './client-details-chart/client-details-contacts';
import { ClientDetailsContactsAddButton } from './client-details-chart/client-details-contacts-add-button';
import { ClientDetailsDiagnosisTreatment } from './client-details-chart/client-details-diagnosis-treatment';
import { ClientDetailsProfileSummary } from './client-details-chart/client-details-profile-summary';
import { ClientDetailsRelationships } from './client-details-chart/client-details-relationships';
import { ClientDetailsSection } from './client-details-chart/client-details-section';

type Props = {
    client: SelectedClient | undefined;
};

function HeaderLinkButton({ children, onClick }: { children: string; onClick?: () => void }) {
    return (
        <Button type="button" variant="ghost" size="sm" className="-mr-2.5" onClick={onClick}>
            {children}
            <ChevronRightIcon className="size-4" />
        </Button>
    );
}

export function ClientDetailsChart({ client }: Props) {
    // The store drops the client on close; keep the last one so the chart survives the slide-out.
    const [retainedClient, setRetainedClient] = useState(client);
    if (client && client.id !== retainedClient?.id) {
        setRetainedClient(client);
    }
    const shown = client ?? retainedClient;
    const openSheet = useSheetStore((state) => state.openSheet);

    const {
        data: profile,
        isLoading,
        isFetching,
    } = useQuery({
        ...clientProfileOptions(shown?.id ?? ''),
        enabled: !!shown?.id,
    });
    const isSavingProfile =
        useIsMutating({ mutationKey: mutationKeys.updateProfile(shown?.id ?? '') }) > 0;
    const {
        data: activity,
        isError: isActivityError,
        isLoading: isActivityLoading,
    } = useQuery({
        ...clientActivityOptions(shown?.id ?? ''),
        enabled: !!shown?.id,
    });
    // Most clients are a contact for no one, so the section and the heading's "Contact for" line
    // are absent for them — and for a read that failed, since there is no empty state to show.
    const { data: relationships, isLoading: isRelationshipsLoading } = useQuery({
        ...clientRelationshipsOptions(shown?.id ?? ''),
        enabled: !!shown?.id,
    });
    const {
        data: contacts,
        isError: isContactsError,
        isLoading: isContactsLoading,
    } = useQuery({
        ...clientContactsOptions(shown?.id ?? ''),
        enabled: !!shown?.id,
    });
    const canReadBalance = useHasAccessSources(['CLINICIAN', 'EHR_BASE']);
    const { data: balance, isLoading: isBalanceLoading } = useQuery({
        ...clientBalanceOptions(shown?.id ?? ''),
        enabled: !!shown?.id && canReadBalance,
    });
    // The cached profile is stale for the whole save and the refetch that follows it, so the body
    // waits for both. The action bar stays on `isLoading`: a save must not grey out its buttons.
    const isDetailsLoading =
        isFetching ||
        isSavingProfile ||
        isActivityLoading ||
        isBalanceLoading ||
        isRelationshipsLoading ||
        isContactsLoading;
    const { data: clientRecord } = useQuery({
        ...clientByIdOptions(shown?.id ?? ''),
        enabled: !!shown?.id,
    });
    const name = clientRecord?.name ?? shown?.name;

    return (
        <>
            <ClientDetailsActionBar client={shown} disabled={isLoading} />
            <div className="min-h-0 flex flex-col flex-1 overflow-y-auto">
                <SheetDescription className="sr-only">Client chart for {name}.</SheetDescription>
                {match(isDetailsLoading)
                    .with(true, () => (
                        <>
                            <SheetTitle className="sr-only">{name}</SheetTitle>
                            <CenteredSpinner label="Loading client chart" />
                        </>
                    ))
                    .with(false, () => (
                        <>
                            <ClientDetailsProfileSummary
                                client={shown}
                                name={name}
                                profile={profile}
                                contactFor={relationships?.items ?? []}
                            />
                            <ClientDetailsSection
                                title="Chart activity"
                                headerAction={<HeaderLinkButton>All Activity</HeaderLinkButton>}
                            >
                                <ClientDetailsChartActivity
                                    client={shown}
                                    activity={activity}
                                    isError={isActivityError}
                                />
                            </ClientDetailsSection>
                            <ClientDetailsSection
                                title="Diagnosis and treatment"
                                headerAction={<HeaderLinkButton>Details</HeaderLinkButton>}
                            >
                                <ClientDetailsDiagnosisTreatment />
                            </ClientDetailsSection>
                            <ClientDetailsSection
                                title="Billing"
                                headerAction={
                                    <HeaderLinkButton
                                        onClick={() => {
                                            if (client) {
                                                openSheet({
                                                    type: 'clients/billing',
                                                    label: 'Billing',
                                                    data: { ...client, name: name ?? client.name },
                                                });
                                            }
                                        }}
                                    >
                                        All Activity
                                    </HeaderLinkButton>
                                }
                            >
                                <ClientDetailsBilling balance={balance} />
                            </ClientDetailsSection>
                            <ClientDetailsSection
                                title="Contacts"
                                headerAction={
                                    <ClientDetailsContactsAddButton
                                        client={shown && name ? { id: shown.id, name } : undefined}
                                    />
                                }
                            >
                                <ClientDetailsContacts
                                    contacts={contacts}
                                    isError={isContactsError}
                                />
                            </ClientDetailsSection>
                            {relationships?.items.length ? (
                                <ClientDetailsSection title="Relationships">
                                    <ClientDetailsRelationships links={relationships.items} />
                                </ClientDetailsSection>
                            ) : null}
                        </>
                    ))
                    .exhaustive()}
            </div>
        </>
    );
}
